import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { phoneNumbersRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { ReleasePhoneNumberDialogComponent } from 'src/app/common-components/release-phone-number-dialog/release-phone-number-dialog.component';
import { AddPhoneNumberComponent } from '../../add-phone-number/containers/add-phone-number.component';
import { PhoneNumbersFilterOption } from '../models/phone-number-filter-option';
import { PhoneNumberOrderingOptions, PhoneNumberRegisterOptions } from '../models/phone-number-register-options';
import { PhoneNumberRegisterListItem, PhoneNumberRegisterResults } from '../models/phone-number-register-results.model';
import { GetPhoneNumbers, GetPhoneNumbers_phoneNumbersConnection_items } from '../queries/generated/GetPhoneNumbers';
import { PhoneNumberRegisterApiService } from '../services/phone-number-register-api.service';
import {
   createPhoneNumber,
   loadAccountPhoneNumbersSuccess,
   loadSystemPhoneNumbersSuccess,
   phoneNumberRegisterAdapterActions,
} from './phone-number-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './phone-number-register.reducer';
import * as PhoneNumberRegisterSelectors from './phone-number-register.selectors';
import { selectFilters } from './phone-number-register.selectors';

@Injectable()
export class PhoneNumberRegisterEffects {
   addPhoneNumber$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createPhoneNumber),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddPhoneNumberComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadPhoneNumberList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(phoneNumberRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(PhoneNumberRegisterSelectors.selectIsOnSystemPage))
         ),
         switchMap(([action, filters, quickFilters, route, orderingOptions, wasPreviouslyOnSystemPage]) => {
            const path = route.url[0].path;
            if (path === phoneNumbersRoute) {
               if (!wasPreviouslyOnSystemPage) {
                  this.store.dispatch(phoneNumberRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadSystemPhoneNumbersSuccess());
            } else {
               if (wasPreviouslyOnSystemPage) {
                  this.store.dispatch(phoneNumberRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(loadAccountPhoneNumbersSuccess());
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadPhoneNumberPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(phoneNumberRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            return this.phoneNumberRegisterApiService
               .getPhoneNumbers(endCursor, filters, quickFilters, orderingOptions)
               .pipe(
                  map((result) => {
                     return phoneNumberRegisterAdapterActions.loadPageSuccess(
                        this.convertGetPhoneNumbersResultToLoadSuccessProps(result.data)
                     );
                  }),
                  catchError((error) => {
                     return of(phoneNumberRegisterAdapterActions.loadPageFailure(error));
                  })
               );
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(phoneNumberRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(PhoneNumberRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickFilters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(phoneNumberRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private phoneNumberRegisterApiService: PhoneNumberRegisterApiService,
      private dialogService: LcsDialogService
   ) {}

   private static concatExtension(extension: { number?: number | undefined; name?: string | null }): string {
      let extensionDisplay = '';
      if (extension.number) {
         extensionDisplay += `${extension.number}`;
      }
      if (extension.name) {
         extensionDisplay += ` - ${extension.name}`;
      }

      return extensionDisplay;
   }

   private convertGetPhoneNumbersResultToLoadSuccessProps(
      getPhoneNumbers: GetPhoneNumbers
   ): PhoneNumberRegisterResults {
      return {
         endCursor: getPhoneNumbers.phoneNumbersConnection.pageInfo.endCursor,
         hasNextPage: getPhoneNumbers.phoneNumbersConnection.pageInfo.hasNextPage,
         items: getPhoneNumbers.phoneNumbersConnection.items.map(this.convertPhoneNumberRegisterListItem),
         totalCount: getPhoneNumbers.phoneNumbersConnection.totalCount,
      };
   }

   private convertPhoneNumberRegisterListItem(
      getPhoneNumberItem: GetPhoneNumbers_phoneNumbersConnection_items
   ): PhoneNumberRegisterListItem {
      return {
         accountName: getPhoneNumberItem.account?.name ?? null,
         closedExtension: PhoneNumberRegisterEffects.concatExtension({ ...getPhoneNumberItem.closedExtension }),
         datePorted: getPhoneNumberItem.datePorted,
         isActive: getPhoneNumberItem.isActive,
         isE911Registered: getPhoneNumberItem.isE911Registered,
         isPorted: getPhoneNumberItem.isPorted,
         location: getPhoneNumberItem.location,
         openExtension: PhoneNumberRegisterEffects.concatExtension({ ...getPhoneNumberItem.openExtension }),
         phoneNumber: getPhoneNumberItem.phoneNumber,
         phoneNumberId: getPhoneNumberItem.phoneNumberId,
         providerId: getPhoneNumberItem.providerId,
         schedule: getPhoneNumberItem.schedule?.name ?? null,
         faxExtension: PhoneNumberRegisterEffects.concatExtension({ ...getPhoneNumberItem.faxExtension }),
         phonesUsingForCallerId: getPhoneNumberItem.phonesUsingForCallerIdConnection?.items.map((item) => {
            return {
               extensionId: item.extension.extensionId,
               number: item.extension.number,
               name: item.extension.name,
               extensionPhoneId: item.extensionPhoneId,
               displayName: item.displayName,
               defaultPhoneNumberId: item.defaultPhoneNumberId,
            };
         }),
         faxesUsingForCallerId: getPhoneNumberItem.faxesUsingForCallerIdConnection?.items.map((item) => {
            return {
               extensionId: item.extension.extensionId,
               number: item.extension.number,
               name: item.extension.name,
               extensionFaxId: item.extensionFaxId,
               defaultPhoneNumberId: item.defaultPhoneNumberId,
            };
         }),
      };
   }

   openReleasePhoneNumberDialog(phoneExtensionList: any, faxExtensionList: any): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(ReleasePhoneNumberDialogComponent, {
         closeOnNavigation: true,
         data: {
            phoneExtensionList,
            faxExtensionList,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }

   private loadNewData(
      filters: PhoneNumbersFilterOption[],
      quickFilters: PhoneNumberRegisterOptions | null = null,
      orderingOptions: PhoneNumberOrderingOptions[] | null = null
   ) {
      return this.phoneNumberRegisterApiService.getPhoneNumbers(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return phoneNumberRegisterAdapterActions.loadSuccess(
               this.convertGetPhoneNumbersResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(phoneNumberRegisterAdapterActions.loadFailure(error));
         })
      );
   }
}
